import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import * as uuid from 'uuid';
import { TutorialStoreService } from './tutorial-store.service';
import { getDemoResponse, TUTORIAL_DEMO_DATA, _buildTutoBilans, _buildTutoBilanProductivite, _buildTutoTravaux, _buildTutoPalette, _buildTutoExpeditionBuilder, _buildTutoBreakdown, _buildTutoChargement, _buildTutoChargementPlanning, _buildTutoBL, _buildTutoFacture, _buildTutoStockForArticle, _buildTutoTracaGraph, _buildTutoMargeAnalysis, _buildTutoAchatAnalysis, _buildTutoFabricationAnalysis } from './tutorial-sandbox-data';

/**
 * HTTP interceptor that creates a "sandbox" during tutorial demo mode.
 *
 * When `TutorialStoreService.isDemoMode()` is `true`:
 * - **GET** requests to `/api/` paths return mock data from the registry
 *   (see `tutorial-sandbox-data.ts`). If no mock is registered the request
 *   passes through to the real backend so existing reads still work.
 * - **POST / PUT / PATCH** requests are intercepted and return a simulated
 *   success (200) with the sent body + a generated `id`. The created entity
 *   is stored in an in-memory session map so subsequent GETs can include it.
 * - **DELETE** requests return a simulated success (200) with an empty body.
 *
 * Requests that do NOT target `/api/` paths (assets, CDN, etc.) always pass through.
 */
@Injectable()
export class TutorialSandboxInterceptor implements HttpInterceptor {
  private store = inject(TutorialStoreService);

  constructor() {
    this._patchFetchForTracaStream();
  }

  /**
   * Le composant de traçabilité charge le graphe via un `fetch()` SSE brut
   * (EventSource-like), qui contourne les HttpInterceptor Angular. En mode démo
   * ce fetch atteint le vrai backend et renvoie un graphe vide pour le lot
   * fictif. On patche donc `window.fetch` : en démo, toute requête vers
   * `/tracabilite/.../stream` renvoie un flux SSE synthétique contenant le
   * graphe de démo (un seul événement `complete`).
   */
  private _patchFetchForTracaStream(): void {
    const w = window as any;
    if (w.__tutoFetchPatched) return;
    w.__tutoFetchPatched = true;
    const originalFetch = w.fetch.bind(w);
    const store = this.store;
    w.fetch = (input: any, init?: any) => {
      const url = typeof input === 'string' ? input : input?.url ?? '';
      if (store.isDemoMode() && /\/tracabilite\/.*\/stream/.test(url)) {
        const payload = { type: 'complete', ..._buildTutoTracaGraph() };
        const body = `data: ${JSON.stringify(payload)}\n\n`;
        return Promise.resolve(new Response(body, {
          status: 200,
          headers: { 'Content-Type': 'text/event-stream' },
        }));
      }
      return originalFetch(input, init);
    };
  }

  /**
   * In-memory store of entities created during the demo session.
   * Key = a normalised "collection" path (e.g. `/api/articles`),
   * Value = array of bodies that were POSTed.
   */
  private sessionEntities = new Map<string, any[]>();
  private _lastClearVersion = 0;

  /**
   * Endpoints infra qui doivent toujours passer au vrai backend,
   * même en mode démo (auth, profil, permissions, config app, WebSocket).
   */
  private static readonly PASSTHROUGH_PATTERNS = [
    '/api/auth/',
    '/api/user_infos',
    '/api/pass',
    '/api/tutorials/',
    // NB : /api/dashboard/ et /api/widget/ NE sont PLUS en passthrough — ils
    // sont mockés pour le tuto tableau-de-bord (voir MOCK_ROUTES).
    '/api/document/',
    '/api/version',
    '/api/search',
  ];

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // --- Clear session entities when a new module starts ---
    const clearVersion = this.store.clearSessionVersion();
    if (clearVersion !== this._lastClearVersion) {
      this._lastClearVersion = clearVersion;
      this.clearSession();
    }

    // --- Pass through when demo mode is off ---
    if (!this.store.isDemoMode()) {
      return next.handle(req);
    }

    // --- Only intercept /api/ calls ---
    const url = this.extractPath(req.url);
    if (!url.includes('/api/')) {
      return next.handle(req);
    }


    // --- Always pass through infra endpoints ---
    if (TutorialSandboxInterceptor.PASSTHROUGH_PATTERNS.some(p => url.includes(p))) {
      return next.handle(req);
    }

    const method = req.method.toUpperCase();

    switch (method) {
      case 'GET':
        return this.handleGet(req, next, url);
      case 'POST':
        return this.handlePost(req, url);
      case 'PUT':
      case 'PATCH':
        return this.handlePutPatch(req);
      case 'DELETE':
        return this.handleDelete();
      default:
        return next.handle(req);
    }
  }

  // ── GET ────────────────────────────────────────────────────────────────

  private handleGet(
    req: HttpRequest<any>,
    next: HttpHandler,
    url: string,
  ): Observable<HttpEvent<any>> {
    // 0. Stateful mocks (dépendent des mouvements palette du tuto préparation-expédition)
    const breakdownMatch = url.match(/\/prepreparation\/breakdown\/commande\/([0-9a-f-]+)/i);
    if (breakdownMatch) {
      const body = _buildTutoBreakdown(breakdownMatch[1], this._paletteLotMouvements, this.store.demoProgress());
      return of(new HttpResponse({ status: 200, body }));
    }

    // 0a2. Tuto planification des composants : stock par article, uniquement
    // quand ce module est actif (évite tout effet de bord sur les autres tutos
    // qui partagent le même order).
    if (url.match(/\/api\/stock\/?$/) &&
        this.store.activeConfig()?.moduleId === 'planification-composants') {
      return of(new HttpResponse({ status: 200, body: _buildTutoStockForArticle() }));
    }

    // 0b. Tuto livraison : chargement + BL stateful (créés via POST). Tant que
    // le chargement n'est pas créé, on laisse les mocks statiques renvoyer [].
    if (this._chargementEtat) {
      if (url.includes('/chargements/planning')) {
        return of(new HttpResponse({ status: 200, body: _buildTutoChargementPlanning(this._chargementEtat) }));
      }
      if (url.match(/\/chargements\/id\/[0-9a-f-]+/i) || url.match(/\/chargements\/[0-9a-f-]+$/i)) {
        return of(new HttpResponse({ status: 200, body: _buildTutoChargement(this._chargementEtat) }));
      }
      if (url.includes('/bonLivraisons/search')) {
        const data = [_buildTutoBL(this._chargementEtat === 'CHARGE')];
        return of(new HttpResponse({ status: 200, body: { data, total: data.length } }));
      }
    }

    // 0c. Tuto facturation (order 21) : le BL généré par la livraison est en
    // attente de facturation, et la facture créée apparaît dans la liste.
    const inFacturationModule = this.store.demoProgress() >= 20.99;
    if (inFacturationModule || this._chargementEtat) {
      if (url.includes('/bonLivraisons/not_billed')) {
        // Plus de BL en attente une fois la facture créée.
        return of(new HttpResponse({ status: 200, body: this._factureCreee ? [] : [_buildTutoBL(true)] }));
      }
      if (url.includes('/factures/search')) {
        const data = this._factureCreee ? [_buildTutoFacture()] : [];
        return of(new HttpResponse({ status: 200, body: { data, total: data.length } }));
      }
      if (url.match(/\/factures\/id\/eager\/[0-9a-f-]+/i)) {
        return of(new HttpResponse({ status: 200, body: _buildTutoFacture() }));
      }
      if (url.includes('/factures/kpis')) {
        return of(new HttpResponse({ status: 200, body: { enAttenteCount: 0, enAttenteTTC: 0, echeanceDepasseeCount: 0, echeanceDepasseeTTC: 0, echeancesDepassees: [] } }));
      }
    }

    // 1. Check static mock registry (pass progress for progressive data)
    const mockBody = getDemoResponse(url, this.store.demoProgress());
    if (mockBody !== null) {
      // If mock is an array, append any session-created entities for this collection
      const collectionKey = this.collectionKey(url);
      const sessionItems = this.sessionEntities.get(collectionKey) ?? [];
      let body = Array.isArray(mockBody) ? [...mockBody, ...sessionItems] : mockBody;
      // Enrich controles with answered state
      body = this._enrichControlesState(body);
      // Enrich PDCB/poste with saved bilans (consoLots quantiteRestante)
      body = this._enrichBilansState(body, url);
      return of(new HttpResponse({ status: 200, body }));
    }

    // 2. Check session entities (collection listing without a static mock)
    const collectionKey = this.collectionKey(url);
    const sessionItems = this.sessionEntities.get(collectionKey);
    if (sessionItems && sessionItems.length > 0) {
      // Detail URL (ends with UUID) → return first item as object, not array
      const isDetailUrl = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/?$/i.test(url);
      const body = isDetailUrl ? sessionItems[0] : sessionItems;
      return of(new HttpResponse({ status: 200, body }));
    }

    // 3. No mock found — return empty array for collection-looking URLs, null otherwise
    const isDetailUrl = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\/?$/i.test(url);
    const body = isDetailUrl ? null : [];
    return of(new HttpResponse({ status: isDetailUrl ? 404 : 200, body }));
  }

  // ── POST ───────────────────────────────────────────────────────────────

  private handlePost(req: HttpRequest<any>, url: string): Observable<HttpEvent<any>> {
    // --- Return a known demo entity for specific POST endpoints ---
    const demoBody = this._demoPostResponse(url, req.body);
    const body = demoBody ?? { ...(req.body ?? {}), id: uuid.v4() };

    // Store entity in session so subsequent GETs include it immediately
    const collectionKey = this.collectionKey(url);
    const existing = this.sessionEntities.get(collectionKey) ?? [];
    this.sessionEntities.set(collectionKey, [...existing, body]);

    return of(new HttpResponse({ status: 200, body }));
  }

  // ── PUT / PATCH ────────────────────────────────────────────────────────

  private handlePutPatch(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    const body = { ...(req.body ?? {}), id: req.body?.id ?? uuid.v4() };
    return of(new HttpResponse({ status: 200, body }));
  }

  // ── DELETE ─────────────────────────────────────────────────────────────

  private handleDelete(): Observable<HttpEvent<any>> {
    return of(new HttpResponse({ status: 200, body: {} }));
  }

  // ── POST response helpers ───────────────────────────────────────────

  /** Counter for sequential article creation (Lait → Biscuits) */
  private _articleCreationCount = 0;
  private _ofCreationCount = 0;
  /** Number of PEOs that have been ended (for stepper auto-advance) */
  private _peoEndCount = 0;
  /** IDs of controles that have been answered as CONFORME */
  private _answeredControleIds = new Set<string>();
  /** Mouvements PREPA_EXPEDITION enregistrés via /prepare_palette (tuto préparation-expédition) */
  private _paletteLotMouvements: any[] = [];
  /** État du chargement du tuto livraison : null (pas créé) | EN_PREPARATION | PRET | CHARGE */
  private _chargementEtat: string | null = null;
  /** Facture créée depuis le BL (tuto facturation) */
  private _factureCreee = false;

  /**
   * Returns a known demo entity for specific POST endpoints.
   * Progress-based: no milestones, just returns the next entity in sequence.
   */
  private _demoPostResponse(url: string, reqBody?: any): any | null {
    const data = TUTORIAL_DEMO_DATA;

    // Analyses (POST) — tuto popups d'analyse marge / achat / fabrication
    if (url.includes('/article-marge-analysis/')) {
      return _buildTutoMargeAnalysis();
    }
    if (url.includes('/article-achat-analysis/')) {
      return _buildTutoAchatAnalysis();
    }
    if (url.includes('/article-fabrication-analysis/')) {
      return _buildTutoFabricationAnalysis();
    }

    // Article creation — return entities sequentially.
    // Matching par articleId (stable) et non par denomination, car les libellés
    // sont personnalisables via le profil prospect (PROSPECT_PROFILE).
    if (url.includes('/specification_etape/create_master') || url.includes('/api/articles')) {
      // ART_LAIT puis ART_BISCUITS (ordre de création du tuto gestion-articles)
      const sequence = [
        'e5f6a7b8-c9d0-4e1f-2a3b-4c5d6e7f8a9b',
        'f6a7b8c9-d0e1-4f2a-3b4c-000000000001',
      ];
      const articleId = sequence[this._articleCreationCount];
      if (articleId) {
        this._articleCreationCount++;
        if (url.includes('/api/articles')) {
          return data.articles.find((a: any) => a.id === articleId) ?? null;
        }
        return data.specificationMasters.find((s: any) => s.articleId === articleId) ?? null;
      }
    }

    // ControlePod creation — return a valid empty pod
    if (url.includes('/controle_pod/create')) {
      return { id: crypto.randomUUID(), controles: [], isActive: true, version: 1 };
    }

    // Specification approval (Sans Validation mode) — return the SE with etat EN_COURS
    if (url.includes('/specification_etape/approuve/')) {
      const seId = url.split('/specification_etape/approuve/')[1]?.split('?')[0];
      for (const sem of data.specificationMasters) {
        const se = sem.versions?.find((v: any) => v.id === seId);
        if (se) {
          se.etat = 'EN_COURS';
          return se;
        }
      }
      return null;
    }

    // OF creation — return a new OF based on request body + sandbox data
    if (url.includes('/api/of') && !url.includes('/api/of/')) {
      const newOf = {
        ...data.ofs[0],
        id: crypto.randomUUID(),
        numero: 'OF' + (2600002 + this._ofCreationCount),
        etat: 'EN_ATTENTE_PLANNIF',
        quantite: reqBody?.quantite ?? 500,
        quantiteProduite: 0,
        started: false,
        dateDebut: reqBody?.dateDebutPrevue ?? null,
        dateFin: null,
      };
      this._ofCreationCount++;
      // Also store under /api/of/nav and /api/of/navfilter so GET list includes it
      for (const key of ['/api/of/nav', '/api/of/navfilter']) {
        const existing = this.sessionEntities.get(key) ?? [];
        this.sessionEntities.set(key, [...existing, newOf]);
      }
      return newOf;
    }

    // Ligne creation — return the static ligne entity
    if (url.includes('/api/lignes')) {
      return data.lignes[0] ?? null;
    }

    // Poste creation (create_new_ope) — return PDCB with new poste
    if (url.includes('/poste/create_new_ope')) {
      const pdcb = this._buildPdcbWithPoste(data, reqBody, 'PENDING');
      // Store the poste and PEOs so subsequent GETs can find them
      if (pdcb?.of?.postes?.[0]) {
        const poste = pdcb.of.postes[0];
        this.sessionEntities.set('/api/poste/eager', [poste]);
        // Store PEOs for /peo/search
        if (poste.processesEtapeDone?.length) {
          this.sessionEntities.set('/api/peo/search', poste.processesEtapeDone);
        }
      }
      return pdcb;
    }

    // Poste start — return PDCB with poste EN_COURS
    if (url.includes('/poste/start')) {
      return this._buildPdcbWithPoste(data, reqBody, 'EN_COURS');
    }

    // Poste end — return PDCB with poste TERMINEE
    if (url.includes('/poste/end')) {
      return this._buildPdcbWithPoste(data, reqBody, 'TERMINEE');
    }

    // Poste update — return PDCB
    if (url.includes('/poste/update')) {
      return this._buildPdcbWithPoste(data, reqBody, reqBody?.etat ?? 'EN_COURS');
    }

    // PEO end — mark a process step as DONE, increment counter, return full PEO
    if (url.includes('/peo/end/')) {
      const peoId = url.split('/peo/end/')[1]?.split('?')[0];
      this._peoEndCount++;
      // Find the full PEO from session (stored by create_new_ope) to return it with all fields
      const storedPeos = this.sessionEntities.get('/api/peo/search') ?? [];
      const existingPeo = storedPeos.find((p: any) => p.id === peoId);
      if (existingPeo) {
        existingPeo.dateFin = new Date().toISOString();
        existingPeo.state = 'DONE';
        return { ...existingPeo };
      }
      // Fallback — return PEO with fields the store needs for filtering
      return {
        id: peoId, state: 'DONE',
        dateFin: new Date().toISOString(), dateDebut: new Date().toISOString(),
        posteId: reqBody?.posteId ?? null,
      };
    }

    // PEO start — mark a process step as IN_PROGRESS, return full PEO
    if (url.includes('/peo/start/')) {
      const peoId = url.split('/peo/start/')[1]?.split('?')[0];
      const storedPeos = this.sessionEntities.get('/api/peo/search') ?? [];
      const existingPeo = storedPeos.find((p: any) => p.id === peoId);
      if (existingPeo) {
        existingPeo.dateDebut = new Date().toISOString();
        existingPeo.state = 'IN_PROGRESS';
        return { ...existingPeo };
      }
      return {
        id: peoId, state: 'IN_PROGRESS',
        dateDebut: new Date().toISOString(), dateFin: null,
        posteId: reqBody?.posteId ?? null,
      };
    }

    // Controle answer — return the controle with etat CONFORME and remember it
    if (url.includes('/controle/answer') || url.includes('/checklist/answer/controle/')) {
      const id = reqBody?.id ?? crypto.randomUUID();
      this._answeredControleIds.add(id);
      // Also remember by parentControl (the template ID) so re-fetches match
      if (reqBody?.parentControl) this._answeredControleIds.add(reqBody.parentControl);
      return { ...(reqBody ?? {}), id, etat: 'CONFORME' };
    }

    // Controle loadForPodIds — return empty array (controles are embedded in PEO pods)
    if (url.includes('/controle/podIds')) {
      return [];
    }

    // Controle buildControlesForPods — return controles from requested pods
    if (url.includes('/controle/build')) {
      const podIds = reqBody?.podIds?.map((p: any) => p.podId).filter(Boolean) ?? [];
      if (podIds.length && data.controlePods) {
        return podIds.flatMap((podId: string) => {
          const pod = data.controlePods.find((p: any) => p.id === podId);
          return pod?.controles ?? [];
        });
      }
      return [];
    }

    // Changement lot refresh — return changement + full PDCB
    if (url.includes('/poste/changement/refresh')) {
      const changement = { id: crypto.randomUUID(), ...reqBody };
      const pdcb = this._buildPdcbWithPoste(data, reqBody, 'EN_COURS');
      return { changementLot: changement, pdcb };
    }

    // Changement lot — return the changement
    if (url.includes('/poste/changement')) {
      return { id: crypto.randomUUID(), ...reqBody };
    }

    // OF end prod / continue_after_bilan (validate is handled separately below)
    if (url.includes('/of/end_prod/') || url.includes('/of/continue_after_bilan/')) {
      const ofId = url.split('/').pop();
      const of = data.ofs.find((o: any) => o.id === ofId);
      if (!of) return { id: ofId };
      const lotBiscuits = data.lots.find((l: any) => l.articleId === data.articles[5]?.id);
      return {
        ...of,
        etat: url.includes('/of/validate/') ? 'VALIDE' : 'EN_ATTENTE_REINTEGRATION',
        started: true, withQuantiteOpe: true,
        quantiteProduite: 250,
        qttReinteTemp: 250,
        lots: lotBiscuits ? [lotBiscuits] : [],
        integrations: of.integrations ?? [],
        postes: of.postes ?? [],
        bilans: of.bilans ?? [],
        mouvements: of.mouvements ?? [],
      };
    }

    // Integration PF — reintegrer expects a PiedDeChaineBuilder
    if (url.includes('/integrations/reintegre/')) {
      const pdcb = this._buildPdcbWithPoste(data, { quantiteProduite: 250, quantiteProduitePoste: 250 }, 'TERMINEE');
      if (pdcb) {
        this._enrichPdcbWithBilans(pdcb, 'EN_ATTENTE_VALIDATION');
      }
      return pdcb;
    }

    // Integration PF (other)
    if (url.includes('/integrations/')) {
      return { id: crypto.randomUUID(), ...reqBody };
    }

    // Bilan matière — saveBilans() expects a PiedDeChaineBuilder response
    if (url.includes('/poste/bilans')) {
      const bilans = reqBody?.bilans ?? [];
      this.sessionEntities.set('_bilans_saved', bilans);

      const pdcb = this._buildPdcbWithPoste(data, { quantiteProduite: 250, quantiteProduitePoste: 250 }, 'TERMINEE');
      if (pdcb) {
        this._enrichPdcbWithBilans(pdcb, 'EN_ATTENTE_REINTEGRATION');
      }
      return pdcb;
    }

    // Travail — create with IJ + PDCB reload (used by hours entry dialog)
    if (url.includes('/calcul_cascade/update/travail')) {
      const pdcb = this._buildPdcbWithPoste(data, { quantiteProduite: 250, quantiteProduitePoste: 250 }, 'TERMINEE');
      if (pdcb) {
        this._enrichPdcbWithBilans(pdcb, 'EN_ATTENTE_VALIDATION');
        pdcb.of.bilanProductivite = [_buildTutoBilanProductivite()];
      }
      return pdcb;
    }

    // Bilan productivité update — return PDCB with bilanProductivite
    if (url.includes('/calcul_cascade/update/bp')) {
      const pdcb = this._buildPdcbWithPoste(data, { quantiteProduite: 250, quantiteProduitePoste: 250 }, 'TERMINEE');
      if (pdcb) {
        this._enrichPdcbWithBilans(pdcb, 'EN_ATTENTE_VALIDATION');
        pdcb.of.bilanProductivite = [_buildTutoBilanProductivite()];
      }
      return pdcb;
    }

    // OF validate — return the OF with etat VALIDE (validateOF expects an OF, not a PDCB)
    if (url.includes('/of/validate/')) {
      const of = data.ofs[0];
      if (!of) return { id: url.split('/').pop() };
      return {
        ...of,
        etat: 'VALIDE',
        started: true, withQuantiteOpe: true,
        quantiteProduite: 250, qttReinteTemp: 250,
        valideProd: true, valideQualite: true,
        bilans: _buildTutoBilans(250),
        bilanProductivite: [_buildTutoBilanProductivite()],
        integrations: [], postes: [], lots: [], mouvements: [],
      };
    }

    // Commande fournisseur ligne — upsert (used by commande-ligne-form save)
    if (url.includes('/commandes/lignes') || url.includes('/commandes/ligne')) {
      return { id: reqBody?.id ?? crypto.randomUUID(), ...reqBody };
    }

    // Commande client — create (returns a full CC with numero + etat EN_REDACTION)
    if (url.includes('/commandes_client/create') || (url.match(/\/commandes_client\/?$/) && !url.includes('/ligne'))) {
      return {
        ...(reqBody ?? {}),
        id: crypto.randomUUID(),
        numero: 'CC2600003',
        etat: 'EN_REDACTION',
        lignes: [],
        dateAnnulation: null,
        dateCreation: new Date().toISOString(),
        entrepots: reqBody?.entrepots ?? [],
      };
    }

    // Commande client ligne — upsert (used by commande-client-ligne-form save)
    if (url.includes('/commandes_client/ligne')) {
      return { id: reqBody?.id ?? crypto.randomUUID(), ...reqBody, remiseLignes: reqBody?.remiseLignes ?? [] };
    }

    // Expedition unit type — create
    if (url.includes('/expedition_unit_type')) {
      return { id: crypto.randomUUID(), ...reqBody, isActive: true, version: 1 };
    }

    // Article Fourni — upsert
    if (url.includes('/article_fourni')) {
      return { id: crypto.randomUUID(), ...reqBody };
    }

    // Tuto livraison — création / progression du chargement
    if (url.includes('/chargements/new')) {
      this._chargementEtat = 'EN_PREPARATION';
      return _buildTutoChargement('EN_PREPARATION');
    }
    if (url.includes('/chargements/add_palettes')) {
      this._chargementEtat = this._chargementEtat ?? 'EN_PREPARATION';
      return _buildTutoChargement(this._chargementEtat);
    }
    if (url.includes('/chargements/pret') || url.includes('/chargements/declare_all_ready')) {
      this._chargementEtat = 'PRET';
      return _buildTutoChargement('PRET');
    }
    if (url.includes('/chargements/expedier')) {
      this._chargementEtat = 'CHARGE';
      return _buildTutoChargement('CHARGE');
    }
    if (url.includes('/chargements/save_expedition_state')) {
      return _buildTutoChargement(this._chargementEtat ?? 'PRET');
    }
    if (url.match(/\/chargements\/?$/) && !url.includes('/chargements/')) {
      // POST /chargements (create/update)
      this._chargementEtat = this._chargementEtat ?? 'EN_PREPARATION';
      return _buildTutoChargement(this._chargementEtat);
    }

    // Tuto facturation — création / émission de la facture
    if (url.includes('/factures/bl/create')) {
      this._factureCreee = true;
      return _buildTutoFacture();
    }
    if (url.includes('/factures/figer/')) {
      const f = _buildTutoFacture();
      f.etat = 'EMISE';
      return f;
    }
    if (url.match(/\/factures\/bl\/factureId\//)) {
      return _buildTutoFacture();
    }

    // Prepare palette — record mouvements, return the palette enriched with the lot.
    // The facade upserts the returned palette → the card updates live;
    // the maker then reloads the builder (handled below) → the line shows 100/100.
    if (url.includes('/prepare_palette')) {
      const mvts = reqBody?.mouvements ?? [];
      this._paletteLotMouvements.push(...mvts);
      return _buildTutoPalette(this._paletteLotMouvements);
    }

    // Expedition builder — stateful: includes the lots placed on the palette
    if (url.includes('/expedition_commande/build')) {
      return _buildTutoExpeditionBuilder(reqBody, this._paletteLotMouvements);
    }

    return null;
  }

  /**
   * Build a mock PiedDeChaineBuilder with a poste in the given state.
   * Used for create_new_ope, start, end, update responses.
   */
  private _buildPdcbWithPoste(data: any, reqBody: any, etat: string): any {
    const of = data.ofs[0];
    if (!of) return null;

    const posteId = reqBody?.id ?? crypto.randomUUID();
    const processEtapes = data.processEtapes ?? [];
    // Reproduce backend createForOpeId logic:
    // 1. Etapes with controles get an empty ControlePod (just {id})
    // 2. PEO gets podId pointing to the empty pod
    // 3. Controles are built dynamically by the front via buildControlesForPods
    const peos = processEtapes.map((pe: any, i: number) => {
      const hasControles = pe.controles?.length > 0;
      const podId = hasControles ? crypto.randomUUID() : undefined;
      // Use _peoEndCount to mark already-ended PEOs with dateFin (so the stepper shows green)
      const isEnded = etat === 'TERMINEE' || (etat === 'EN_COURS' && i < this._peoEndCount);
      const isActive = etat === 'EN_COURS' && i === this._peoEndCount;
      return {
        id: crypto.randomUUID(),
        dateDebut: etat !== 'PENDING' ? new Date().toISOString() : null,
        dateFin: isEnded ? new Date().toISOString() : null,
        poste: { id: posteId },
        posteId,
        processEtape: pe,
        processEtapeId: pe.id,
        podId: podId ?? null,
        pod: podId ? { id: podId, controles: [], isActive: true, version: 1 } : null,
        state: isEnded ? 'DONE' : (isActive ? 'IN_PROGRESS' : 'PENDING'),
        optional: pe.optional ?? false,
        checklist: null,
        startUserId: null,
        endUserId: null,
      };
    });

    const poste = {
      id: posteId,
      index: 0,
      denomination: reqBody?.denomination ?? 'Opé. 1',
      etat,
      dateDebut: etat !== 'PENDING' ? new Date().toISOString() : null,
      dateFin: etat === 'TERMINEE' ? new Date().toISOString() : null,
      quantitePrevue: reqBody?.quantitePrevue ?? of.quantite,
      quantiteProduite: reqBody?.quantiteProduite ?? 0,
      quantiteProduitePoste: reqBody?.quantiteProduitePoste ?? 0,
      processesEtapeDone: peos,
      changementsLot: [],
      controles: [],
      controlesEvenement: [],
      controlesCalages: [],
      etapesCalages: [],
      consoLots: [],
      controleConso: [],
      integration: [],
      intervals: [],
      podInitial: null,
      podStart: null,
      podEnd: null,
      score: reqBody?.score ?? 0,
      commentaire: reqBody?.commentaire ?? '',
      commentairePositif: reqBody?.commentairePositif ?? '',
      commentaireNegatif: reqBody?.commentaireNegatif ?? '',
      of,
      ligne: data.lignes?.[0] ?? null,
      userInfos: null,
      isActive: true,
      version: 1,
    };

    const nomenclatureLignes = data.nomenclatureLignes ?? [];
    const artLots = nomenclatureLignes.map((nl: any) => {
      const art = data.articles.find((a: any) => a.id === nl.articleId);
      const ai = art?.articleInfos;
      const lots = data.lots.filter((l: any) => l.articleId === nl.articleId)
        .map((l: any) => ({
          ...l,
          stocks: [{ id: 'stk-' + l.id, lotId: l.id, quantite: l.quantite, adresseId: 'adr-pdc-001' }],
        }));
      const semId = art?.specificationEtapeMasterId;
      // For PDC articles, populate the pdc array with lots that have stock at the PDC address
      const pdc = ai?.withPiedDeChaine
        ? lots.map((l: any) => ({ ...l, stocks: [{ id: 'stk-pdc-' + l.id, lotId: l.id, quantite: l.quantite, adresseId: 'adr-pdc-001' }] }))
        : [];
      return { articleInfos: ai, articleId: nl.articleId, lots, pdc, tiersId: null, specificationEtapeRefId: semId ? (semId + '-se') : null };
    });

    return {
      adresse: { id: 'adr-pdc-001', denomination: 'PDC Ligne Biscuits' },
      lot: data.lots.find((l: any) => l.articleId === data.articles[5]?.id),
      articlesLots: artLots,
      nomenclature: { nomenclatureLignes },
      path: {
        id: 'path-biscuits',
        quantityProdDefault: 250,
        processEtapes,
        nomenclatureLignes,
        articleInfos: data.articles[5]?.articleInfos,
        podControlesProductionIds: [],
        podControlesCalageIds: [],
        podControlesFinIds: [],
        podControlesStartOpeIds: [],
        podControlesEndOpeIds: [],
        process: [{ process: processEtapes }],
        checklists: [],
      },
      of: {
        ...of,
        etat: etat === 'PENDING' ? of.etat : 'EN_PROD',
        started: etat !== 'PENDING',
        withQuantiteOpe: true,
        quantiteProduite: reqBody?.quantiteProduite ?? of.quantiteProduite ?? 0,
        postes: [poste],
        integrations: [],
        bilans: [],
        bilanProductivite: [],
        mouvements: [],
        lots: etat !== 'PENDING' ? [data.lots.find((l: any) => l.articleId === data.articles[5]?.id)] : [],
      },
    };
  }

  // ── Helpers ────────────────────────────────────────────────────────────

  /**
   * Extracts the pathname from a URL string (handles both absolute and relative).
   */
  private extractPath(rawUrl: string): string {
    try {
      const parsed = new URL(rawUrl, window.location.origin);
      return parsed.pathname;
    } catch {
      return rawUrl;
    }
  }

  /**
   * Normalises a URL to a "collection" key by stripping a trailing UUID segment
   * (e.g. `/api/articles/550e8400-...` -> `/api/articles`).
   */
  private collectionKey(url: string): string {
    // Strip trailing UUID-shaped segment
    return url.replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
      '',
    );
  }

  /** Clears all session entities (called when leaving demo mode). */
  clearSession(): void {
    this.sessionEntities.clear();
    this._articleCreationCount = 0;
    this._peoEndCount = 0;
    this._answeredControleIds.clear();
    this._paletteLotMouvements = [];
    this._chargementEtat = null;
    this._factureCreee = false;
  }

  /**
   * Enrich a PDCB with realistic bilan data (costs, consoLots, OF totals).
   * Used by /poste/bilans and /integrations/reintegre/ handlers.
   */
  private _enrichPdcbWithBilans(pdcb: any, ofEtat: string): void {
    const bilans = _buildTutoBilans(250);
    pdcb.of.bilans = bilans;
    pdcb.of.qttReinteTemp = 250;
    pdcb.of.etat = ofEtat;
    pdcb.of.bilanProductivite = [];

    // Inject consoLots into the poste
    const poste = pdcb.of.postes?.[0];
    if (poste) {
      const consoLots: any[] = [];
      for (const bilan of bilans) {
        for (const bp of (bilan.bilanParPoste ?? [])) {
          for (const cl of (bp.consoLots ?? [])) {
            consoLots.push({ ...cl, articleId: bilan.articleId });
          }
        }
      }
      poste.consoLots = consoLots;
    }

    // Compute OF-level cost totals from bilans
    let coutTheo = 0, coutReel = 0;
    for (const b of bilans) {
      coutTheo += (b.coutUnitaire ?? 0) * (b.consoTheorique ?? 0);
      coutReel += (b.coutUnitaire ?? 0) * (b.consoProd ?? 0);
    }
    pdcb.of.coutMatiereTotalTheorique = coutTheo;
    pdcb.of.coutMatiereTotalReel = coutReel;
    pdcb.of.coutPersonnelTotalTheorique = 45;
    pdcb.of.coutPersonnelTotalReel = 47.5;
    pdcb.of.scoreMatiere = coutTheo > 0 ? Math.round((coutReel / coutTheo) * 100) : 100;
    pdcb.of.scorePersonnel = 105;
    pdcb.of.scoreTotal = pdcb.of.scoreMatiere;
    pdcb.of.scoreMatiereSurcout = coutReel - coutTheo;
    pdcb.of.scorePersonnelSurcout = 2.5;
    pdcb.of.coutNcMatiere = 0;
    pdcb.of.coutNcPersonnel = 0;
    pdcb.of.coutNcRecuperable = 0;
  }

  /**
   * Recursively set etat='CONFORME' on any controle object whose id
   * matches a previously answered controle. Works on arrays, objects
   * with `controles` arrays, and pod structures.
   */
  /**
   * Inject saved bilan consoLots into PDCB responses so the bilan matière
   * dialog shows the saisie done in the conso helper.
   */
  private _enrichBilansState(data: any, url: string): any {
    const savedBilans = this.sessionEntities.get('_bilans_saved');
    if (!savedBilans || !Array.isArray(savedBilans)) return data;
    if (!data) return data;

    // Only enrich PDCB-like responses (of/pdc, poste/eager, end_prod)
    if (!url.includes('/of/pdc/') && !url.includes('/poste/eager/') && !url.includes('/of/end_prod/')) {
      return data;
    }

    // The saved bilans contain consoLots with quantiteRestante
    // Inject them into the PDCB's of.postes[].consoLots
    try {
      const of = data.of ?? data;
      if (of?.postes) {
        for (const poste of of.postes) {
          if (!poste.consoLots) poste.consoLots = [];
          for (const bilan of savedBilans) {
            if (bilan.bilanParPoste) {
              for (const bp of bilan.bilanParPoste) {
                if (bp.consoLots) {
                  for (const cl of bp.consoLots) {
                    // Find or add the consoLot
                    const existing = poste.consoLots.find((c: any) => c.lotId === cl.lotId && c.articleId === bilan.articleId);
                    if (existing) {
                      existing.quantiteRestante = cl.quantiteRestante;
                    } else if (cl.quantiteRestante !== null && cl.quantiteRestante !== undefined) {
                      poste.consoLots.push({ ...cl, articleId: bilan.articleId });
                    }
                  }
                }
              }
            }
          }
        }
      }
    } catch { /* ignore enrichment errors */ }

    return data;
  }

  private _enrichControlesState(data: any): any {
    if (this._answeredControleIds.size === 0) return data;
    if (!data) return data;

    if (Array.isArray(data)) {
      return data.map(item => this._enrichControleItem(item));
    }
    return this._enrichControleItem(data);
  }

  private _enrichControleItem(item: any): any {
    if (!item || typeof item !== 'object') return item;

    // Direct controle match (has id + denomination — looks like a controle)
    if (item.id && item.denomination && this._answeredControleIds.has(item.id)) {
      item = { ...item, etat: 'CONFORME' };
    }

    // Nested controles array (pod, PEO, etc.)
    if (Array.isArray(item.controles)) {
      item = { ...item, controles: item.controles.map((c: any) =>
        c?.id && this._answeredControleIds.has(c.id) ? { ...c, etat: 'CONFORME' } : c
      )};
    }

    // Pod inside a PEO
    if (item.pod?.controles) {
      item = { ...item, pod: { ...item.pod, controles: item.pod.controles.map((c: any) =>
        c?.id && this._answeredControleIds.has(c.id) ? { ...c, etat: 'CONFORME' } : c
      )}};
    }

    return item;
  }
}
